'use client'

import { createContext, use, useCallback, useMemo, useState } from 'react'

interface CheckpointContextValue {
  checkpoints: Set<string>
  completed: Set<string>
  lessonCompleted: boolean
  registerCheckpoint: (id: string) => void
  unregisterCheckpoint: (id: string) => void
  toggleCheckpoint: (id: string) => void
  toggleLessonComplete: () => void
  isComplete: (id: string) => boolean
}

const CheckpointContext = createContext<CheckpointContextValue | null>(null)

export function CheckpointProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [checkpoints, setCheckpoints] = useState<Set<string>>(() => new Set())
  const [completed, setCompleted] = useState<Set<string>>(() => new Set())
  const [lessonCompleted, setLessonCompleted] = useState(false)

  const registerCheckpoint = useCallback((id: string) => {
    setCheckpoints((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  // Remove a checkpoint's id from the registry when it unmounts (e.g. on
  // lesson/course navigation), so `checkpoints`/`completed` reflect only the
  // currently-mounted lesson instead of accumulating every visited lesson's ids.
  const unregisterCheckpoint = useCallback((id: string) => {
    setCheckpoints((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    setCompleted((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const toggleCheckpoint = useCallback((id: string) => {
    setCompleted((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const isComplete = useCallback(
    (id: string) => {
      return completed.has(id)
    },
    [completed],
  )

  const toggleLessonComplete = useCallback(() => {
    setLessonCompleted((prev) => !prev)
  }, [])

  const value = useMemo<CheckpointContextValue>(
    () => ({
      checkpoints,
      completed,
      lessonCompleted,
      registerCheckpoint,
      unregisterCheckpoint,
      toggleCheckpoint,
      toggleLessonComplete,
      isComplete,
    }),
    [
      checkpoints,
      completed,
      lessonCompleted,
      registerCheckpoint,
      unregisterCheckpoint,
      toggleCheckpoint,
      toggleLessonComplete,
      isComplete,
    ],
  )

  return <CheckpointContext value={value}>{children}</CheckpointContext>
}

export function useCheckpoint(): CheckpointContextValue {
  const context = use(CheckpointContext)
  if (!context) {
    throw new Error('useCheckpoint must be used within a CheckpointProvider')
  }
  return context
}
